import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface FinishingMethod {
  id: string;
  code: string;
  name: string;
  description: string;
  engName: string;
}

export interface ContainerPart {
  id: string;
  code: string;
  name: string;
  engName: string;
  sortOrder: number;
  methods: FinishingMethod[];
}

// ─── Filtering rule: which master_code.code to show per container type ───
const NORMAL_CODES = ['01', '02', '03'];       // Cap, Middle Ring, Body
const DROPPER_CODES = ['06', '04', '03'];       // Rubber Bulb, Dropper Cap, Body
const PUMP_CODES = ['01', '07', '02', '03'];    // Cap, Pump, Middle Ring, Body

function detectContainerType(categoryL1: string, categoryL2: string): 'dropper' | 'pump' | 'normal' {
  const combined = `${categoryL1} ${categoryL2}`.toLowerCase();
  if (/dropper|스포이드/.test(combined)) return 'dropper';
  if (/pump|펌프/.test(combined)) return 'pump';
  return 'normal';
}

function filterParts(
  allParts: ContainerPart[],
  categoryL1: string,
  categoryL2: string,
): ContainerPart[] {
  const type = detectContainerType(categoryL1, categoryL2);
  const allowedCodes = type === 'dropper' ? DROPPER_CODES : type === 'pump' ? PUMP_CODES : NORMAL_CODES;
  const allowedSet = new Set(allowedCodes);
  return allParts.filter((p) => allowedSet.has(p.code));
}

export function useContainerParts(categoryL1: string, categoryL2: string) {
  const [parts, setParts] = useState<ContainerPart[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabase) {
        setLoading(false);
        return;
      }

      // Fetch container parts + finish methods in parallel
      const [partsRes, methodsRes] = await Promise.all([
        supabase
          .from('master_codes')
          .select('id, code, name, sort_order, note')
          .eq('code_type', 'container_part')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('master_codes')
          .select('id, code, name, sort_order, note')
          .eq('code_type', 'finish_l1')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
      ]);

      if (partsRes.error) {
        console.error('[useContainerParts] Failed to fetch parts:', partsRes.error.message);
        setLoading(false);
        return;
      }

      // Build finish methods from DB
      const dbMethods: FinishingMethod[] = (methodsRes.data || []).map((row) => ({
        id: `method-${row.code}`,
        code: row.code as string,
        name: (row.name as string) || row.code,
        description: (row.note as string) || '',
        engName: (row.note as string) || '',
      }));

      // Fallback: if no DB methods, show empty (will fallback to mock in page)
      const fallbackMethods: FinishingMethod[] = dbMethods.length > 0 ? [] : [];

      const allParts: ContainerPart[] = (partsRes.data || []).map((row) => ({
        id: `part-${row.code}`,
        code: row.code as string,
        name: (row.name as string) || row.code,
        engName: (row.note as string) || '',
        sortOrder: (row.sort_order as number) || 0,
        methods: dbMethods.length > 0 ? dbMethods : fallbackMethods,
      }));

      const filtered = filterParts(allParts, categoryL1, categoryL2);

      setParts(filtered);
      setLoading(false);
    } catch (err) {
      console.error('[useContainerParts] Error:', err);
      setLoading(false);
    }
  }, [categoryL1, categoryL2]);

  useEffect(() => {
    let cancelled = false;
    if (!cancelled) fetchParts();
    return () => { cancelled = true; };
  }, [fetchParts]);

  return { parts, loading };
}